// src/modules/app-views/crm/pages/Boards/components/DraggableBoardCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Button, Card, Tag, Tooltip } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DraggableBoardCard = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { board, onOpenBoard, onEditBoard, group, canManage } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const boardUid = board.uid || board._id;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: boardUid,
    data: { board, groupUid: group?.uid || group?._id },
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const columnCount = board.columns?.length || 0;

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition: 'transform 200ms ease',
    opacity: isDragging ? 0 : 1,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClick = () => {
    onOpenBoard(board);
  };

  const handleEdit = e => {
    e.stopPropagation();
    onEditBoard(e, board, group);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      ref={setNodeRef}
      style={{ ...sortableStyle, height: '100%' }}
      {...attributes}
      {...listeners}
    >
      <Card
        hoverable
        size="small"
        className="crm-card"
        style={{
          borderTop: `3px solid ${board.backColor || '#1890ff'}`,
          height: '100%',
          marginBottom: 0,
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          flexDirection: 'column',
        }}
        styles={{ body: { flex: 1 } }}
        onClick={handleClick}
        extra={
          canManage ? (
            <Tooltip title={translateX('settings')}>
              <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
                onClick={handleEdit}
                onPointerDown={e => e.stopPropagation()}
                style={{ minWidth: 40, minHeight: 40 }}
              />
            </Tooltip>
          ) : undefined
        }
        title={board.title}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          {board.description ? (
            <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
              {board.description}
            </p>
          ) : (
            <div>&nbsp;</div>
          )}

          <Tag className="mt-3" color="blue" style={{ alignSelf: 'flex-start' }}>
            {columnCount} {translateX(columnCount === 1 ? 'column' : 'columns')}
          </Tag>
        </div>
      </Card>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DraggableBoardCard;
